import React, { useContext, useEffect, useEffectEvent } from 'react';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { ConnectionState } from 'utils/connectionState';
import { getNegotiatedConnection, receiveNotifications } from '../Notifications/signalrConnector';
import { setGeneratedFormDocuments } from 'features/form/formReducer';
import { updateConnection } from 'store/notificationsReducer';
import { PdfProcessedEvent } from 'features/form/types';
import { CampaignUpdatedEvent } from 'Api/Campaigns/Types/types';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';
import { AuthContext } from '@urbanx/agx.auth.client';
import { HUB_NAMES } from 'constants/signalrConstants';

interface SetupNotificationsLayoutProps {
  children: React.ReactNode;
}

export const SetupNotificationsLayout = ({ children }: SetupNotificationsLayoutProps) => {
  const dispatch = useAppDispatch();

  const { userAccount, getAuthToken } = useContext(AuthContext);

  const { connectionState, connectionUpdatedAt } = useAppSelector((state) => state.notifications);

  const markCampaignAsStale = useCampaignStaleMarker();
  const markCampaignAsStaleEvent = useEffectEvent((campaignId: string) =>
    markCampaignAsStale(campaignId)
  );

  // Add a lock to prevent the browser tab from sleeping while it isn't opened
  useEffect(() => {
    if (navigator.locks?.request) {
      let releaseLock: (() => void) | undefined;
      const lock = new Promise<void>((resolve) => {
        releaseLock = resolve;
      });

      void navigator.locks.request('autopilot_shared_lock', { mode: 'shared' }, () => lock);

      return () => releaseLock?.();
    }
  }, []);

  useEffect(() => {
    if (connectionState === ConnectionState.Disconnected) {
      console.error('AutoPilot disconnected.');
    }
  }, [connectionState, connectionUpdatedAt]);

  useEffect(() => {
    if (!userAccount) return;

    let disposed = false;

    dispatch(updateConnection(ConnectionState.Connecting));

    const hubCon = getNegotiatedConnection(HUB_NAMES.APP_NOTIFICATIONS, getAuthToken);

    hubCon.onclose(() => {
      if (!disposed) dispatch(updateConnection(ConnectionState.Disconnected));
    });

    hubCon.onreconnecting(() => {
      if (!disposed) dispatch(updateConnection(ConnectionState.Reconnecting));
    });

    hubCon.onreconnected(() => {
      if (!disposed) dispatch(updateConnection(ConnectionState.Connected));
    });

    receiveNotifications(hubCon, 'PdfProcessedEvent', (data: PdfProcessedEvent) => {
      dispatch(setGeneratedFormDocuments(data));
    });

    receiveNotifications(hubCon, 'CampaignUpdatedEvent', async (data: CampaignUpdatedEvent) => {
      await markCampaignAsStaleEvent(data.campaignId);
    });

    const startConnection = async () => {
      try {
        await hubCon.start();
        if (!disposed) dispatch(updateConnection(ConnectionState.Connected));
      } catch (error) {
        if (!disposed) {
          console.error('Error starting AutoPilot notification connection', error);
          dispatch(updateConnection(ConnectionState.Disconnected));
        }
      }
    };

    void startConnection();

    return () => {
      disposed = true;
      void hubCon.stop().catch((error) => {
        console.error('Error stopping AutoPilot notification connection', error);
      });
    };
  }, [dispatch, getAuthToken, userAccount]);

  return children;
};
